/**
 * GO Fest habitat schedules: which wild Pokémon are out, by the hour, in
 * the trainer's LOCAL time.
 *
 * A GO Fest day rotates through habitats ("Verdant Overgrowth 10 to 11 and
 * 2 to 3"), each with its own shiny-boosted spawns. The event feed carries
 * none of that: an event arrives as one flat spawn list (for the Mega
 * Finale, its Mega raid bosses), so the Shiny Hunt block could only ever say
 * "GO Fest is on". This is the schedule, curated per event from LeekDuck's
 * event page (2026-09-04 owner ask), with the same auditable shape the
 * bonuses use: sources and a verifiedAt.
 *
 * Every time here is wall-clock LOCAL: GO runs a fest by the player's own
 * clock, so a habitat window is a local date plus hours, and the helpers
 * take the device's `Date` apart with its local getters. Nothing here is in
 * UTC on purpose.
 *
 * Pure: no store, no DOM.
 */
import { speciesName } from "@/data/speciesNames";

export interface HabitatWindow {
  /** Local calendar day, YYYY-MM-DD. */
  day: string;
  /** Local hour the window opens (0..23), inclusive. */
  startHour: number;
  /** Local hour the window closes, exclusive. */
  endHour: number;
}

export interface GoFestHabitat {
  key: string;
  name: string;
  /** The habitat's colour, as the event art and LeekDuck's icons draw it (hex). */
  color: string;
  /** National dex ids, in the page's order. All are shiny-boosted. */
  speciesIds: number[];
  windows: HabitatWindow[];
}

export interface GoFestHabitatSchedule {
  /** The live feed's event id this schedule belongs to. */
  eventId: string;
  /**
   * Local hour of the schedule's first day from which the Shiny Hunt block
   * reads the fest as LIVE, ahead of the feed's own start (owner direction
   * 2026-09-05: the Mega Finale goes live at midnight, not 10 a.m., so the
   * block is up the whole fest morning). Omit to follow the feed.
   */
  liveFromHour?: number;
  habitats: GoFestHabitat[];
  sources: readonly string[];
  verifiedAt: string;
}

const SAT = "2026-09-05";
const SUN = "2026-09-06";
const KALOS_STARTERS = [650, 653, 656];
const slot = (day: string, first: number, second: number): HabitatWindow[] => [
  { day, startHour: first, endHour: first + 1 },
  { day, startHour: second, endHour: second + 1 },
];

/**
 * GO Fest 2026: Mega Finale. Four habitats a day, each on twice, one hour
 * at a time; the Kalos starters sit in every habitat. Saturday and Sunday
 * have different habitats. Times 10 a.m. to 6 p.m. local on both days.
 */
export const MEGA_FINALE_HABITATS: GoFestHabitatSchedule = {
  eventId: "pokemon-go-fest-2026-mega-finale",
  liveFromHour: 0,
  sources: ["https://leekduck.com/events/pokemon-go-fest-2026-mega-finale/"],
  verifiedAt: "2026-09-04",
  habitats: [
    {
      key: "verdant-overgrowth",
      color: "#3FA34D",
      name: "Verdant Overgrowth",
      speciesIds: [13, 69, 123, 127, 315, 459, 543, ...KALOS_STARTERS, 672],
      windows: slot(SAT, 10, 14),
    },
    {
      key: "mindworks-canal",
      color: "#E0559B",
      name: "Mindworks Canal",
      speciesIds: [63, 79, 120, 280, 307, ...KALOS_STARTERS, 677, 688, 692],
      windows: slot(SAT, 11, 15),
    },
    {
      key: "eerie-alley",
      color: "#7A5FB5",
      name: "Eerie Alley",
      speciesIds: [92, 228, 353, 570, 607, ...KALOS_STARTERS, 686, 708, 710],
      windows: slot(SAT, 12, 16),
    },
    {
      key: "circuit-plaza",
      color: "#E3B341",
      name: "Circuit Plaza",
      speciesIds: [25, 179, 309, 587, 602, 618, ...KALOS_STARTERS, 694, 702],
      windows: slot(SAT, 13, 17),
    },
    {
      key: "iron-frostworks",
      color: "#5FA8C9",
      name: "Iron Frostworks",
      speciesIds: [95, 227, 304, 361, 374, 529, ...KALOS_STARTERS, 698, 712],
      windows: slot(SUN, 10, 14),
    },
    {
      key: "battle-district",
      color: "#D9552F",
      name: "Battle District",
      speciesIds: [66, 318, 322, 427, 551, 559, ...KALOS_STARTERS, 674, 870],
      windows: slot(SUN, 11, 15),
    },
    {
      key: "skyline-roosts",
      color: "#3F8FE0",
      name: "Skyline Roosts",
      speciesIds: [129, 142, 147, 333, 371, ...KALOS_STARTERS, 661, 696, 714],
      windows: slot(SUN, 12, 16),
    },
    {
      key: "prism-promenade",
      color: "#B57BD6",
      name: "Prism Promenade",
      speciesIds: [25, 35, 302, 303, 531, ...KALOS_STARTERS, 676, 682, 703],
      windows: slot(SUN, 13, 17),
    },
  ],
};

const SCHEDULES: readonly GoFestHabitatSchedule[] = [MEGA_FINALE_HABITATS];

/** The habitat schedule for a live-feed event id, or null when it has none. */
export function habitatScheduleFor(eventId: string): GoFestHabitatSchedule | null {
  return SCHEDULES.find((s) => s.eventId === eventId) ?? null;
}

/** Local calendar day of `now` as YYYY-MM-DD. */
export function localDayKey(now: number): string {
  const d = new Date(now);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Local epoch ms for a window's opening moment. */
export function windowStartMs(w: HabitatWindow): number {
  const [y, m, d] = w.day.split("-").map(Number);
  return new Date(y, m - 1, d, w.startHour, 0, 0, 0).getTime();
}

export function windowEndMs(w: HabitatWindow): number {
  const [y, m, d] = w.day.split("-").map(Number);
  return new Date(y, m - 1, d, w.endHour, 0, 0, 0).getTime();
}

export interface HabitatNow {
  /** The habitat on right now, local time, or null between/outside windows. */
  current: { habitat: GoFestHabitat; window: HabitatWindow; endsAt: number } | null;
  /** The next habitat to open after `now`, or null once the fest is over. */
  next: { habitat: GoFestHabitat; window: HabitatWindow; startsAt: number } | null;
  /** Today's rotation (local day), in opening order. Empty on a non-fest day. */
  today: Array<{ habitat: GoFestHabitat; window: HabitatWindow }>;
}

/** Where the rotation stands at `now`, by the device's local clock. */
export function habitatsAt(schedule: GoFestHabitatSchedule, now: number = Date.now()): HabitatNow {
  const all: Array<{ habitat: GoFestHabitat; window: HabitatWindow }> = [];
  for (const habitat of schedule.habitats) {
    for (const window of habitat.windows) all.push({ habitat, window });
  }
  all.sort((a, b) => windowStartMs(a.window) - windowStartMs(b.window));
  const day = localDayKey(now);
  const today = all.filter((x) => x.window.day === day);
  const live = all.find((x) => windowStartMs(x.window) <= now && now < windowEndMs(x.window));
  const upcoming = all.find((x) => windowStartMs(x.window) > now);
  return {
    current: live ? { habitat: live.habitat, window: live.window, endsAt: windowEndMs(live.window) } : null,
    next: upcoming
      ? { habitat: upcoming.habitat, window: upcoming.window, startsAt: windowStartMs(upcoming.window) }
      : null,
    today,
  };
}

/** The habitats on a local day, in opening order, each with that day's windows. */
export function habitatsForDay(
  schedule: GoFestHabitatSchedule,
  day: string,
): Array<{ habitat: GoFestHabitat; windows: HabitatWindow[] }> {
  const out: Array<{ habitat: GoFestHabitat; windows: HabitatWindow[] }> = [];
  for (const habitat of schedule.habitats) {
    const windows = habitat.windows.filter((w) => w.day === day).sort((a, b) => a.startHour - b.startHour);
    if (windows.length > 0) out.push({ habitat, windows });
  }
  return out.sort((a, b) => a.windows[0].startHour - b.windows[0].startHour);
}

/**
 * Device-local epoch ms from which the fest reads as live for the Shiny
 * Hunt block: `liveFromHour` on the schedule's first day, or Infinity when
 * the schedule sets none (the feed's start decides).
 */
export function scheduleLiveFromMs(schedule: GoFestHabitatSchedule): number {
  if (schedule.liveFromHour == null) return Infinity;
  const first = scheduleDays(schedule)[0];
  if (!first) return Infinity;
  return windowStartMs({ day: first, startHour: schedule.liveFromHour, endHour: schedule.liveFromHour });
}

/** The schedule's days, in order. */
export function scheduleDays(schedule: GoFestHabitatSchedule): string[] {
  const out: string[] = [];
  for (const h of schedule.habitats) for (const w of h.windows) if (!out.includes(w.day)) out.push(w.day);
  return out.sort();
}

/** Every species the schedule boosts, first appearance first. */
export function habitatSpeciesIds(schedule: GoFestHabitatSchedule): number[] {
  const out: number[] = [];
  for (const h of schedule.habitats) for (const id of h.speciesIds) if (!out.includes(id)) out.push(id);
  return out;
}

/** "10 am" / "2 pm" (owner direction 2026-09-04: no full stops; the block's uppercase styling prints AM / PM). */
export function habitatHourLabel(hour: number): string {
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12} ${hour < 12 ? "am" : "pm"}`;
}

/** A species name for a habitat tile, from the same table every other tile uses. */
export function habitatSpeciesName(id: number): string {
  return speciesName(id);
}
